import { useModals } from '@/components/modals/ModalProvider';
import FleetTitle from '@/libs/antd/components/Title';
import {
	searchPlakats,
	submitPlakats,
	SearchPlakatParams,
	SearchPlakatItem,
	SubmitPlakatItemRequest,
} from '@/libs/fleet-api/services/plakat';
import {
	Button,
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Table,
	message,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';

const PlakatListPage: FC = () => {
	const { openCustomerDetail } = useModals();

	// Search state
	const [searchParams, setSearchParams] = useState<SearchPlakatParams>({
		pagination: {
			pageSize: 10,
			pageNumber: 1,
		},
		search: {
			by: '',
			value: '',
		},
	});

	// Loading states
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Selected items for submission with their dates
	const [selectedItems, setSelectedItems] = useState<{
		[key: number]: { id: number; releasedDate: string };
	}>({});

	// Data state
	const [data, setData] = useState<{
		totalCount: number;
		pageSize: number;
		currentPage: number;
		totalPages: number;
		items?: SearchPlakatItem[];
	} | null>(null);

	// Search function
	const handleSearch = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await searchPlakats(searchParams);
			setData(response.data);
		} catch (error) {
			message.error('Gagal mengambil data plakat');
			console.error('Error searching plakats:', error);
		} finally {
			setIsLoading(false);
		}
	}, [searchParams]);

	// Handle pagination changes
	const handleTableChange = (pageNumber: number, pageSize: number) => {
		setSearchParams((prev) => ({
			...prev,
			pagination: {
				pageNumber,
				pageSize,
			},
		}));
	};

	// Handle search form submission
	const handleFormSubmit = (values: { searchBy: string; keyword: string }) => {
		setSearchParams((prev) => ({
			...prev,
			search: {
				by: values.searchBy ?? undefined,
				value: values.keyword ?? undefined,
			},
			pagination: {
				...prev.pagination,
				pageNumber: 1, // Reset to first page when searching
			},
		}));
	};

	// Handle item selection for submission
	const handleSelectItem = (record: SearchPlakatItem, checked: boolean) => {
		if (!record.id) {
			message.error('Invalid plakat record: missing ID');
			return;
		}

		setSelectedItems((prev) => {
			if (checked) {
				return {
					...prev,
					[record.id!]: {
						id: record.id!,
						releasedDate: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
					},
				};
			} else {
				const newState = { ...prev };
				delete newState[record.id!];
				return newState;
			}
		});
	};

	// Handle date change for selected item
	const handleDateChange = (
		record: SearchPlakatItem,
		date: dayjs.Dayjs | null,
	) => {
		if (!record.id) {
			message.error('Invalid plakat record: missing ID');
			return;
		}

		if (!date) {
			// If date is cleared, remove the item from selection
			setSelectedItems((prev) => {
				const newState = { ...prev };
				delete newState[record.id!];
				return newState;
			});
			return;
		}

		// When date is set, automatically add/update the record in selectedItems
		setSelectedItems((prev) => ({
			...prev,
			[record.id!]: {
				id: record.id!,
				releasedDate: date.format('YYYY-MM-DDTHH:mm:ss'),
			},
		}));
	};

	// Handle submission
	const handleSubmit = async () => {
		const selectedCount = Object.keys(selectedItems).length;
		if (selectedCount === 0) {
			message.warning('Pilih setidaknya satu item untuk disubmit');
			return;
		}

		// Check if all selected items have both checkbox selected AND dates filled
		const itemsWithoutDates = Object.values(selectedItems).filter(
			(item) => !item.releasedDate,
		);

		if (itemsWithoutDates.length > 0) {
			message.warning(
				'Tanggal rilis plakat harus diisi untuk semua item yang dipilih',
			);
			return;
		}

		setIsSubmitting(true);
		try {
			const submitData: SubmitPlakatItemRequest[] =
				Object.values(selectedItems);
			const response = await submitPlakats({ plakats: submitData });
			if (response.status === 'OK') {
				message.success('Plakat berhasil disubmit');
				setSelectedItems({});
				handleSearch(); // Refresh data
			} else {
				message.error(response.data.message || 'Plakat berhasil disubmit');
			}
		} catch (error) {
			message.error('Gagal submit plakat');
			console.error('Error submitting plakats:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Search when searchParams change
	useEffect(() => {
		handleSearch();
	}, [searchParams, handleSearch]);

	// Table columns
	const columns: ColumnsType<SearchPlakatItem> = [
		{
			title: 'No',
			align: 'center',
			fixed: 'left',
			width: 60,
			render: (_, __, index) =>
				(searchParams.pagination.pageNumber - 1) *
					searchParams.pagination.pageSize +
				index +
				1,
		},
		{
			title: 'No. PO',
			dataIndex: 'purchaseOrderNo',
			render: (value) => value || '-',
		},
		{
			title: 'Tanggal PO',
			dataIndex: 'purchaseOrderDate',
			align: 'center',
			render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
		},
		{
			title: 'Status PO',
			dataIndex: 'purchaseOrderStatus',
			align: 'center',
			render: (value) => value || '-',
		},
		{
			title: 'Nama Customer',
			render: (_, record) => {
				return record.customerId ? (
					<a
						href="#"
						onClick={(e) => {
							e.preventDefault();
							openCustomerDetail(record.customerId!);
						}}
					>
						{record.customerName}
					</a>
				) : (
					record.customerName || '-'
				);
			},
		},
		{
			title: 'Jenis Unit',
			dataIndex: 'unitType',
			render: (value) => value || '-',
		},
		{
			title: 'No. Plakat',
			dataIndex: 'plakatNo',
			render: (value) => value || '-',
		},
		{
			title: 'Tanggal Rilis Plakat',
			dataIndex: 'releasedDate',
			align: 'center',
			render: (_, record) => {
				const isSelected = selectedItems[record.id || 0];
				const hasExistingDate = !!record.releasedDate;

				// If item is not actionable, show existing date or dash
				if (!record.isAllowAction) {
					return record.releasedDate ? (
						<DatePicker
							value={dayjs(record.releasedDate)}
							format="DD/MM/YYYY"
							style={{ width: 150 }}
							disabled
						/>
					) : (
						'-'
					);
				}

				// If item has existing date, show it (disabled)
				if (hasExistingDate) {
					return (
						<DatePicker
							value={dayjs(record.releasedDate)}
							format="DD/MM/YYYY"
							style={{ width: 150 }}
							disabled
						/>
					);
				}

				// Otherwise show datepicker for selected items
				return (
					<DatePicker
						format="DD/MM/YYYY"
						placeholder="Pilih Tanggal"
						value={
							isSelected?.releasedDate ? dayjs(isSelected.releasedDate) : null
						}
						onChange={(date) => handleDateChange(record, date)}
						disabled={hasExistingDate}
						style={{ width: 150 }}
						allowClear={!hasExistingDate}
					/>
				);
			},
		},
		{
			title: 'FRM',
			dataIndex: 'frmName',
			render: (value) => value || '-',
		},
		{
			title: 'FBDH',
			dataIndex: 'fbdhName',
			render: (value) => value || '-',
		},
		{
			title: 'Checklist Plakat',
			align: 'center',
			render: (_, record) => {
				if (!record.isAllowAction)
					return (
						<Checkbox
							checked
							disabled
						/>
					);

				const isSelected = selectedItems[record.id || 0];

				return (
					<Checkbox
						checked={!!isSelected}
						onChange={(e) => handleSelectItem(record, e.target.checked)}
						disabled={!record.isAllowAction}
					/>
				);
			},
		},
	];

	return (
		<Fragment>
			<FleetTitle style={{ marginBottom: 20 }}>Pencetakan Plakat</FleetTitle>

			<Form
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={handleFormSubmit}
			>
				<Row gutter={[20, 20]}>
					<Col flex="none">
						<Form.Item
							label={<span>Kata Kunci</span>}
							labelCol={{ flex: 'auto', style: { width: 100 } }}
							wrapperCol={{ flex: 'none', style: { width: 200 } }}
							name="searchBy"
							initialValue="customer-name"
						>
							<Select
								options={[
									{ label: 'Nama Customer', value: 'customer-name' },
									{ label: 'No. PO', value: 'purchase-order-no' },
									{ label: 'No. Plakat', value: 'plakat-no' },
								]}
								allowClear
								style={{ width: '100%' }}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						<Form.Item name="keyword">
							<Input placeholder="Search" />
						</Form.Item>
					</Col>
					<Col>
						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Divider />

			<FleetTitle
				level={4}
				style={{ marginBottom: 20 }}
			>
				List Informasi Pencetakan Plakat
			</FleetTitle>

			<Table
				bordered
				scroll={{ x: 'max-content' }}
				loading={isLoading}
				dataSource={data?.items || []}
				columns={columns}
				pagination={{
					total: data?.totalCount || 0,
					current: searchParams.pagination.pageNumber,
					pageSize: searchParams.pagination.pageSize,
					onChange: handleTableChange,
				}}
				rowKey={(record) => record.id || 0}
			/>

			<div style={{ marginTop: 16, textAlign: 'right' }}>
				<Button
					type="primary"
					loading={isSubmitting}
					disabled={Object.keys(selectedItems).length === 0}
					onClick={handleSubmit}
				>
					SUBMIT
				</Button>
			</div>
		</Fragment>
	);
};

export default PlakatListPage;
